import { useEffect, useMemo, useRef, useState } from 'react'
import { m } from 'motion/react'
import { ArrowLeft, Calendar, Check, Clock, Globe, Video } from 'lucide-react'
import { Button, useFeedback } from '@/components/ui'
import { cn } from '@/lib/cn'
import { trpc } from '@/lib/trpc'
import {
  trackBookingComplete,
  trackBookingError,
  trackBookingSlotSelected,
  trackBookingSubmit,
  trackBookingView,
} from '@/lib/analytics'

interface Slot {
  startMs: number
  endMs: number
  startIso: string
  endIso: string
}
interface BookingTypeView {
  id: string
  label: string
  description?: string
  durationMinutes: number
  timeZone: string
}

interface BookingWidgetProps {
  bookingTypeId?: string
  /** Prefill the details form (e.g. from an intake step). */
  prefill?: { name?: string; email?: string }
  /** Extra context stored on the booking + forwarded to CRM/webhook (UTM, source). */
  metadata?: Record<string, string>
  /** Hide the booking-type header when the surrounding page already states it. */
  showHeader?: boolean
  /**
   * Which surface this widget is mounted on — 'book-page' | 'contact-form' |
   * 'intake-wizard'. Passed explicitly rather than read from the URL: this
   * widget mounts inside the contact form and the intake wizard as well as on
   * /book, so "the widget was seen" means something different on each, and a
   * funnel that cannot tell them apart is unreadable. Deliberately NOT
   * `window.location.pathname` — that read is what produced path-shaped
   * traffic sources in GA4 (see lib/analytics.ts).
   */
  analyticsSource?: string
  className?: string
}

type Phase = 'loading' | 'error' | 'unavailable' | 'pick' | 'confirm' | 'done'

const WINDOW_DAYS = 35
const VISITOR_TZ =
  typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'America/Los_Angeles'

export function BookingWidget({
  bookingTypeId = 'strategy-call',
  prefill,
  metadata,
  showHeader = true,
  analyticsSource = 'book-page',
  className,
}: BookingWidgetProps) {
  const { toast } = useFeedback()
  const [phase, setPhase] = useState<Phase>('loading')
  const [bookingType, setBookingType] = useState<BookingTypeView | null>(null)
  const [slots, setSlots] = useState<Slot[]>([])
  const [selectedDay, setSelectedDay] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null)
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' })
  const [submitting, setSubmitting] = useState(false)
  const [confirmation, setConfirmation] = useState<{ when: string; meetingUrl?: string } | null>(null)

  // Funnel-step latches. `booking_widget_view` and `booking_slot_selected` fire
  // ONCE PER MOUNT so their event counts read as people; the view used to fire
  // on every loadAvailability(), which meant the conflict-retry path and the
  // "Try again" button each re-counted the same visitor. `attempt` is the
  // opposite: submits and errors fire every time, because retries are the
  // signal there.
  const viewFiredRef = useRef(false)
  const slotFiredRef = useRef(false)
  const attemptRef = useRef(0)

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, name: prefill.name ?? f.name, email: prefill.email ?? f.email }))
  }, [prefill])

  async function loadAvailability() {
    setPhase('loading')
    try {
      const now = Date.now()
      const res = await trpc.booking.getAvailability.query({
        bookingTypeId,
        fromMs: now,
        toMs: now + WINDOW_DAYS * 86_400_000,
      })
      setBookingType(res.bookingType)
      setSlots(res.slots)
      setPhase('pick')
      if (!viewFiredRef.current) {
        viewFiredRef.current = true
        // slot_count separates "saw times" from "saw an empty calendar" — today
        // those are the same event, so a calendar with no availability is
        // indistinguishable from a healthy view that nobody acted on.
        trackBookingView(bookingTypeId, analyticsSource, res.slots.length)
      }
    } catch (err) {
      // PRECONDITION_FAILED = booking is deliberately off (no calendar creds,
      // e.g. dev). Not a transient failure, so no "Try again".
      if (isUnavailable(err)) {
        setPhase('unavailable')
        trackBookingError(bookingTypeId, analyticsSource, 'unavailable', 0)
        return
      }
      setPhase('error')
      trackBookingError(bookingTypeId, analyticsSource, 'availability_load', 0)
    }
  }

  useEffect(() => {
    void loadAvailability()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingTypeId])

  // Group slots by the visitor's local calendar day.
  const days = useMemo(() => groupByDay(slots), [slots])
  const dayKeys = useMemo(() => Object.keys(days), [days])

  // Default-select the first day with availability.
  useEffect(() => {
    if (dayKeys.length && (!selectedDay || !days[selectedDay])) setSelectedDay(dayKeys[0])
  }, [dayKeys, days, selectedDay])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedSlot) return
    attemptRef.current += 1
    trackBookingSubmit(bookingTypeId, analyticsSource, attemptRef.current)
    setSubmitting(true)
    try {
      const booking = await trpc.booking.book.mutate({
        bookingTypeId,
        startMs: selectedSlot.startMs,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        notes: form.notes.trim() || undefined,
        timeZone: VISITOR_TZ,
        metadata,
      })
      setConfirmation({ when: fmtLong(booking.startMs), meetingUrl: booking.meetingUrl })
      setPhase('done')
      trackBookingComplete(bookingTypeId, analyticsSource, daysAhead(selectedSlot.startMs))
    } catch (err) {
      const conflict = isConflict(err)
      trackBookingError(bookingTypeId, analyticsSource, conflict ? 'slot_conflict' : 'unknown', attemptRef.current)
      if (conflict) {
        toast('That time was just taken. Please pick another.', 'error')
        setSelectedSlot(null)
        setPhase('pick')
        await loadAvailability()
      } else {
        toast('Something went wrong booking that time. Please try again.', 'error')
      }
    } finally {
      setSubmitting(false)
    }
  }

  // -- Render -------------------------------------------------------------

  if (phase === 'loading') {
    return (
      <Shell className={className}>
        <div className="animate-pulse space-y-5" aria-hidden>
          {showHeader && (
            <div className="border-border/60 space-y-2 border-b pb-4">
              <div className="bg-muted-foreground/15 h-5 w-36 rounded" />
              <div className="bg-muted-foreground/10 h-4 w-52 rounded" />
            </div>
          )}
          <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
            <div className="flex gap-2 sm:flex-col">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="bg-muted-foreground/10 h-14 w-28 shrink-0 rounded-lg sm:w-full" />
              ))}
            </div>
            <div className="hidden grid-cols-3 content-start gap-2 sm:grid">
              {[0, 1, 2].map((i) => (
                <div key={i} className="bg-muted-foreground/10 h-11 rounded-lg" />
              ))}
            </div>
          </div>
        </div>
        <p className="sr-only" role="status">
          Loading available times
        </p>
      </Shell>
    )
  }

  if (phase === 'error') {
    return (
      <Shell className={className}>
        <div className="space-y-4 py-12 text-center">
          <p className="text-muted-foreground">We couldn't load available times right now.</p>
          <Button variant="outline" onClick={() => void loadAvailability()}>
            Try again
          </Button>
          <p className="text-muted-foreground text-sm">
            Or email{' '}
            <a className="text-primary underline" href="mailto:hello@fuguelab.com">
              hello@fuguelab.com
            </a>
          </p>
        </div>
      </Shell>
    )
  }

  if (phase === 'unavailable') {
    return (
      <Shell className={className}>
        <div className="text-muted-foreground py-12 text-center">
          Online booking isn't available right now. Email{' '}
          <a className="text-primary underline" href="mailto:hello@fuguelab.com">
            hello@fuguelab.com
          </a>{' '}
          and we'll find a time.
        </div>
      </Shell>
    )
  }

  if (phase === 'done' && confirmation) {
    return (
      <Shell className={className}>
        <m.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 py-8 text-center">
          <div className="bg-success/15 mx-auto flex h-14 w-14 items-center justify-center rounded-full">
            <Check className="text-success h-7 w-7" strokeWidth={3} />
          </div>
          <h3 className="text-foreground text-2xl font-bold">You're booked!</h3>
          <p className="text-muted-foreground">{confirmation.when}</p>
          {confirmation.meetingUrl && (
            <a
              href={confirmation.meetingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary inline-flex items-center gap-2 font-medium underline"
            >
              <Video className="h-4 w-4" /> Join on Google Meet
            </a>
          )}
          <p className="text-muted-foreground text-sm">
            A calendar invitation with the meeting link is on its way to your inbox.
          </p>
        </m.div>
      </Shell>
    )
  }

  const daySlots = selectedDay ? (days[selectedDay] ?? []) : []

  return (
    <Shell className={className}>
      {showHeader && bookingType && (
        <div className="border-border/60 mb-5 border-b pb-4">
          <div className="text-foreground flex items-center gap-2 font-semibold">
            <Calendar className="text-primary h-4 w-4" /> {bookingType.label}
          </div>
          <p className="text-muted-foreground mt-1 flex items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> {bookingType.durationMinutes} min
            </span>
            <span className="inline-flex items-center gap-1">
              <Video className="h-3.5 w-3.5" /> Google Meet
            </span>
          </p>
        </div>
      )}

      {phase === 'pick' && dayKeys.length === 0 && (
        <div className="text-muted-foreground py-10 text-center">
          No open times in the next few weeks. Email{' '}
          <a className="text-primary underline" href="mailto:hello@fuguelab.com">
            hello@fuguelab.com
          </a>{' '}
          and we'll find a time.
        </div>
      )}

      {phase === 'pick' && dayKeys.length > 0 && (
        <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
            {/* Days */}
            <div
              role="group"
              aria-label="Pick a day"
              className="flex gap-2 overflow-x-auto pb-1 sm:max-h-[340px] sm:flex-col sm:overflow-y-auto sm:pb-0"
            >
              {dayKeys.map((key) => {
                const selected = key === selectedDay
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setSelectedDay(key)}
                    className={cn(
                      'focus-visible:ring-ring shrink-0 rounded-lg border px-4 py-2.5 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none',
                      selected
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                    )}
                  >
                    <div className={cn('font-medium', selected && 'text-primary')}>
                      {fmtDayLabel(days[key][0].startMs)}
                    </div>
                    <div className="text-xs opacity-70">{plural(days[key].length, 'time')}</div>
                  </button>
                )
              })}
            </div>

            {/* Times for the selected day */}
            <div className="sm:min-h-[180px]">
              {daySlots.length > 0 && (
                <>
                  <div className="text-foreground mb-2 text-sm font-medium" aria-live="polite">
                    {fmtDayFull(daySlots[0].startMs)}
                  </div>
                  <div className="grid max-h-[300px] grid-cols-2 content-start gap-2 overflow-y-auto pr-1 sm:grid-cols-3">
                    {daySlots.map((slot) => (
                      <button
                        key={slot.startMs}
                        type="button"
                        onClick={() => {
                          setSelectedSlot(slot)
                          setPhase('confirm')
                          if (!slotFiredRef.current) {
                            slotFiredRef.current = true
                            trackBookingSlotSelected(bookingTypeId, analyticsSource, daysAhead(slot.startMs))
                          }
                        }}
                        className="border-border hover:border-primary hover:bg-primary/5 text-foreground focus-visible:ring-ring rounded-lg border px-2 py-3 text-center text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
                      >
                        {fmtTime(slot.startMs)}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <p className="text-muted-foreground border-border/60 flex items-center gap-1.5 border-t pt-3 text-xs">
            <Globe className="h-3.5 w-3.5 shrink-0" aria-hidden /> Times shown in your timezone: {tzLabel()}
          </p>
        </m.div>
      )}

      {phase === 'confirm' && selectedSlot && (
        <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
          <button
            type="button"
            onClick={() => setPhase('pick')}
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex items-center gap-1 rounded text-sm focus-visible:ring-2 focus-visible:outline-none"
          >
            <ArrowLeft className="h-4 w-4" /> Back to times
          </button>

          <div className="border-border bg-muted/10 space-y-1.5 rounded-lg border p-4 text-sm">
            <div className="text-foreground flex items-center gap-2 font-medium">
              <Calendar className="text-primary h-4 w-4 shrink-0" aria-hidden />
              {fmtDayFull(selectedSlot.startMs)}
            </div>
            <div className="text-foreground flex items-center gap-2 font-medium">
              <Clock className="text-primary h-4 w-4 shrink-0" aria-hidden />
              {fmtTime(selectedSlot.startMs)} to {fmtTime(selectedSlot.endMs)}
            </div>
            <div className="text-muted-foreground flex items-center gap-2 text-xs">
              <Globe className="h-4 w-4 shrink-0" aria-hidden />
              {tzLabel()}
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <Field label="Name" htmlFor="bk-name">
              <input
                id="bk-name"
                required
                autoComplete="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Email" htmlFor="bk-email">
              <input
                id="bk-email"
                type="email"
                required
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Phone (optional)" htmlFor="bk-phone">
              <input
                id="bk-phone"
                type="tel"
                autoComplete="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Anything you'd like to cover? (optional)" htmlFor="bk-notes">
              <textarea
                id="bk-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className={cn(inputCls, 'resize-none')}
              />
            </Field>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Booking…' : `Confirm ${fmtTime(selectedSlot.startMs)}`}
            </Button>
            <p className="text-muted-foreground text-center text-xs">
              A calendar invite with the Google Meet link goes straight to your inbox.
            </p>
          </form>
        </m.div>
      )}
    </Shell>
  )
}

// ---------------------------------------------------------------------------

function Shell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn('border-border bg-background mx-auto w-full max-w-2xl rounded-xl border p-5 shadow-sm', className)}
    >
      {children}
    </div>
  )
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      {children}
    </div>
  )
}

const inputCls =
  'border-border bg-background text-foreground focus:ring-primary w-full rounded-md border px-3 py-2 focus:ring-2 focus:outline-none'

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'}`
}

/**
 * How far out the chosen slot is, in days. An analytics dimension, not display
 * copy. Called from event handlers only, never during render: `Date.now()` in a
 * render path is a hydration mismatch on this prerendered site.
 */
function daysAhead(startMs: number): number {
  return Math.round((startMs - Date.now()) / 86_400_000)
}

/** Friendly visitor-timezone label, e.g. "Budapest (GMT+2)". */
function tzLabel(): string {
  const city = VISITOR_TZ.split('/').pop()?.replace(/_/g, ' ') ?? VISITOR_TZ
  const offset = new Intl.DateTimeFormat('en-US', { timeZone: VISITOR_TZ, timeZoneName: 'shortOffset' })
    .formatToParts(new Date())
    .find((p) => p.type === 'timeZoneName')?.value
  return offset ? `${city} (${offset})` : city
}

function groupByDay(slots: Slot[]): Record<string, Slot[]> {
  const out: Record<string, Slot[]> = {}
  for (const s of slots) {
    const key = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: VISITOR_TZ,
    }).format(new Date(s.startMs))
    ;(out[key] ??= []).push(s)
  }
  return out
}

function fmtDayLabel(ms: number): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: VISITOR_TZ,
  }).format(new Date(ms))
}

function fmtDayFull(ms: number): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: VISITOR_TZ,
  }).format(new Date(ms))
}

function fmtTime(ms: number): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: VISITOR_TZ,
  }).format(new Date(ms))
}

function fmtLong(ms: number): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: VISITOR_TZ,
  }).format(new Date(ms))
}

function isUnavailable(err: unknown): boolean {
  const data = (err as { data?: { code?: string; httpStatus?: number } }).data
  return data?.code === 'PRECONDITION_FAILED' || data?.httpStatus === 412
}

function isConflict(err: unknown): boolean {
  const data = (err as { data?: { code?: string; httpStatus?: number } }).data
  return data?.code === 'CONFLICT' || data?.httpStatus === 409
}
